/**
 * @fileoverview Small square thumbnail with image-or-initials fallback.
 * @module shared/components/ui/Thumbnail
 */

import { ReactElement, useState } from 'react'
import { cn } from '@/shared/lib/utils'

export interface ThumbnailProps {
  /** Image URL if available */
  src?: string | null
  /** Alt/title used to compute initials when image missing */
  alt: string
  /** Size in px (width = height) @default 32 */
  size?: number
  /** Optional extra classes */
  className?: string
}

export function Thumbnail({ src, alt, size = 32, className }: ThumbnailProps): ReactElement {
  const [error, setError] = useState(false)
  const showImage = Boolean(src) && !error
  const initials = alt ? alt.trim().replace(/^\W+/, '').slice(0, 2).toUpperCase() : ''

  const style = { width: `${size}px`, height: `${size}px` }

  return (
    <div className={cn('rounded-sm overflow-hidden shrink-0', className)} style={style}>
      {showImage ? (
        <img
          src={src as string}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setError(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted/20 text-muted-foreground text-[11px] font-medium">
          {initials || '—'}
        </div>
      )}
    </div>
  )
}
